import { IScreenControllerTranslations } from "./__types__/UIViewController";
import { IReactionDisposer } from "mobx";
import { UILogicController } from "@view/root/__proto__/withController";
import { IRootTreeModel } from "@models/RootTreeModel";
import { RequestStatus } from "@constants/repositories";

import {
  ITranslationsListLocalFilters,
  ITranslationsListViewModel,
} from "@viewModels/TranslationsListViewModel/__types__/ITranslationsListViewModel";
import { TranslationsListViewModel } from "@viewModels/TranslationsListViewModel/TranslationsListViewModel";
import { IPagination } from "@viewModels/pagination/types";
import { ITranslateEntity } from "@entities/TranslateEntity";
import { ScenesViewModel } from "@viewModels/scenes/ScenesViewModel";
import { SceneControllerTranslationEdit } from "@scenes/TranslationEditScene";
import { message } from "antd";
import { TranslationService } from "@services/translate";
import { Utils } from "@modules/utils";
import { clone } from "mobx-state-tree";
import { ErrorScene, ScenesErrors } from "@viewModels/scenes/ErrorScene";

class ScreenControllerTranslations
  extends UILogicController
  implements IScreenControllerTranslations
{
  public disposers: { [key: string]: IReactionDisposer } = {};

  private TranslationsListViewModel: ITranslationsListViewModel;

  public constructor(model: IRootTreeModel) {
    super(model);

    this.TranslationsListViewModel = new TranslationsListViewModel(model);
  }

  /** Indicator for otp request confirmation or resending otp request */
  public get isRequestInProgress(): boolean {
    return (
      this.TranslationsListViewModel.statuses.getStatus("fetchItems") ===
      RequestStatus.Pending
    );
  }

  public get filters(): { search: string } {
    return this.TranslationsListViewModel.filters;
  }

  public get listOfTranslationsMetadata(): IPagination<ITranslateEntity> {
    return this.TranslationsListViewModel.metadata;
  }

  public get listOfFilteredTranslations(): ITranslateEntity[] {
    return this.TranslationsListViewModel.listFiltered;
  }

  public setFilterSearch = (search: string): void => {
    this.TranslationsListViewModel.setFilterSearch(search);
  };

  public setFilterBySegment(
    segment: ITranslationsListLocalFilters["segment"]
  ): void {
    this.TranslationsListViewModel.setFilterBySegment(segment);
  }

  public fetchListOfTranslations = async (): Promise<void> => {
    try {
      await this.TranslationsListViewModel.fetchItemsBatch(
        this.TranslationsListViewModel.meta.current_page + 1,
        false
      );
    } catch (error) {
      message.error(
        Utils.translateIfLanguageKey(Utils.getMessageFromError(error))
      );
    }
  };

  public refreshListOfTranslations = async (): Promise<void> => {
    try {
      await this.TranslationsListViewModel.fetchItemsBatch(0, true);
    } catch (error) {
      message.error(
        Utils.translateIfLanguageKey(Utils.getMessageFromError(error))
      );
    }
  };

  public resetFilters = async (): Promise<void> => {
    try {
      this.TranslationsListViewModel.setFilterSearch("");

      await this.TranslationsListViewModel.fetchItemsBatch(1, true);
    } catch (error) {
      message.error(
        Utils.translateIfLanguageKey(Utils.getMessageFromError(error))
      );
    }
  };

  public openTranslationCreateScene = async (): Promise<void> => {
    try {
      await ScenesViewModel.openRegisteredScene(
        SceneControllerTranslationEdit,
        {
          model: this.model,
          translate: undefined,
        }
      );

      message.success(
        TranslationService.t("txt_successfully_created_entity", {
          entity: TranslationService.t("txt_translate"),
        })
      );

      /** Fetch list of translate after successfully changing translates list */
      await this.refreshListOfTranslations();
    } catch (error) {
      if (error instanceof ErrorScene) {
        if (
          [
            ScenesErrors.UserClosedScene,
            ScenesErrors.SceneClosedByCallback,
          ].includes(error.cause as ScenesErrors)
        ) {
          return;
        }
      }

      message.error(
        Utils.translateIfLanguageKey(Utils.getMessageFromError(error))
      );
    }
  };

  public openTranslationEditScene = async (
    translate: ITranslateEntity
  ): Promise<void> => {
    try {
      await ScenesViewModel.openRegisteredScene(
        SceneControllerTranslationEdit,
        {
          model: this.model,
          translate: clone(translate),
        }
      );

      message.success(
        TranslationService.t("txt_successfully_updated_entity", {
          entity: TranslationService.t("txt_translate"),
        })
      );

      /** Fetch list of translate after successfully changing translates list */
      await this.refreshListOfTranslations();
    } catch (error) {
      if (error instanceof ErrorScene) {
        if (
          [
            ScenesErrors.UserClosedScene,
            ScenesErrors.SceneClosedByCallback,
          ].includes(error.cause as ScenesErrors)
        ) {
          return;
        }
      }

      message.error(
        Utils.translateIfLanguageKey(Utils.getMessageFromError(error))
      );
    }
  };

  public beforeDestroy = (): void => {
    this.TranslationsListViewModel.beforeDestroy();

    /** Cancel all disposers */
    Object.values(this.disposers).map((func) => func.call(undefined));
  };
}

export { ScreenControllerTranslations };
